import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    Input,
    NgZone,
    ViewChild,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { delay, filter, pairwise, take, tap } from 'rxjs/operators';

import { AuthService } from '../../../../auth/auth.service';
import { NoteEntity } from '../../../../coaching-base/config';
import {
    CoachingDiffDecorateConnector,
    CoachingDiffDecoratorService,
    DiffItem,
} from '../../../../coaching-base/diff-decorator';
import { EntityType, NoteCategory } from '../../../../coaching-base/general';
import { NoteDetailFormValue } from '../../../../coaching-note/note-detail-form-value.model';
import { NotesComponent } from '../../../../coaching-note/notes.component';
import { CmpsForm } from '../../../../coaching-observation/experimental.form';
import { Row } from '../../../../coaching-observation/experimental.grid';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../../common/take-until-destroy.decorator';
import { StateToProps } from '../../../../store';
import { useDispatcher, UseStore } from '../../../../store.decorator';

import {
    NarrativeCommitmentFormFields,
    NarrativeCommitmentFormValue,
} from './commitment-narrative.form';
import { TranslationKeyMap } from './commitment-narrative.i18n';
import {
    CellContext,
    makeRows,
    RowContext,
} from './commitment-narrative.model';
import { commitmentNarrativeReducer } from './commitment-narrative.reducer';
import { State } from './commitment-narrative.state';
import { sortNotesByCreatedOnDesc } from './functions/sort-notes-commitment.function.';
import { DateFilterType } from './models/data-filter-type.enum';
import { RowActionType } from './models/row-action-type.enum';
import { addNote } from './state/add-note.action';
import { applyFilters } from './state/filter.action';
import { reload } from './state/reload.action';
import { toggleExpandNote } from './state/toggle-expand.action';
import { updateOrRemoveNote } from './state/update-row.action';
import { useSelector } from './store-use-selector';

@Component({
    selector: 'cmps-commitment-narrative',
    templateUrl: './commitment-narrative.component.html',
    host: {
        class: 'cmps-commitment-narrative',
        'data-e2e': 'component-commitment-narrative',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CommitmentNarrativeComponent),
            multi: true,
        },
    ],
})
@TakeUntilDestroy()
@UseStore('coachingDetail.commitmentNarrative', commitmentNarrativeReducer)
export class CommitmentNarrativeComponent
    implements
        StateToProps<State>,
        ControlValueAccessor,
        TakeUntilDestroy,
        CoachingDiffDecorateConnector
{
    state: State;

    @Input() noteEntity: NoteEntity;
    @ViewChild(NotesComponent) _noteComponent: NotesComponent;

    private _sub: Subscription;
    private readonly _controlSub: Map<FormGroup, Subscription> = new Map();
    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _NoteCategory = NoteCategory;
    /** @private */ _dateFilterType = DateFilterType;
    /** @private */ _disabled: boolean;
    /** @private */ _noteComponentDisabled: boolean;
    /** @private */ _initialNotesLength: number;
    /** @private */ _shouldFocusOnNote: boolean;
    private _onChange: (_: NoteDetailFormValue[]) => void;
    private _onTouched: () => void;

    _form: CmpsForm<
        NarrativeCommitmentFormFields,
        NarrativeCommitmentFormValue
    >;

    constructor(
        private readonly _formBuilder: FormBuilder,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _ngZone: NgZone,
        private readonly _cdr: ChangeDetectorRef,
        private readonly _diffDecoratorService: CoachingDiffDecoratorService,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
    ) {
        const formGroup = this._formBuilder.group({
            note: this._formBuilder.array([]),
        });
        this._form = new CmpsForm(
            this._translateService,
            formGroup,
            NarrativeCommitmentFormFields,
            'commitmentNarrative',
        );

        this._shouldFocusOnNote = false;
        this._noteComponentDisabled = false;
    }

    // Lifecycle hooks
    ngOnInit(): void {
        this._diffDecoratorService.registerForEntityType(EntityType.Note, this);

        this._initRowsSubscription();
        this._initFilterRowsSubscription();
        this._initFocusNoteSubscription();
    }

    ngAfterViewChecked(): void {
        if (this._noteComponent != null && !this._noteComponentDisabled) {
            this._disableNoteComponentAndUpdateCD();
        }

        if (this._shouldFocusOnNote && this.state.focusNoteId != null) {
            this._focusOnNote();
        }
    }

    ngOnDestroy(): void {
        this._diffDecoratorService.unRegisterForEntityType(
            EntityType.Note,
            this,
        );
        this._sub?.unsubscribe();
        this._controlSub.forEach((sub) => sub.unsubscribe());
    }

    // ControlValueAccessor methods
    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._disabled = isDisabled;
    }

    writeValue(obj: NoteDetailFormValue[]): void {
        const items: NoteDetailFormValue[] =
            obj == null ? [] : sortNotesByCreatedOnDesc(obj);
        const rows = makeRows(items);
        this._populateForm(rows);
        useDispatcher(this, reload({ rows }));

        this._ngZone.onStable.pipe(take(1)).subscribe({
            complete: () => this._diffDecoratorService.redecorate(this),
        });
    }

    getNoteControl(index: number): FormControl | null {
        const notesArray = this._form.form.get(
            this._form.fields.NOTES.name,
        ) as FormArray;
        if (notesArray?.at(index) != null) {
            const control = notesArray
                .at(index)
                .get('notes')
                ?.get('value') as FormControl;

            if (this._disabled) {
                control.disable();
            } else {
                control.enable();
            }

            return control;
        }
        return null;
    }

    ddcDecorate(
        diffItem: DiffItem,
        state: 'change' | 'accept' | 'dispute',
    ): void {
        if (diffItem.diffItemId == null) {
            return;
        }

        const el = this._el.nativeElement.querySelector(
            `[data-diffitemid="${diffItem.diffItemId}"]`,
        );
        if (el == null) {
            return;
        }

        el.classList.add(`cmps-commitment-narrative--dd-note-${state}`);
    }

    ddcUnDecorate(
        diffItem: DiffItem,
        state: 'change' | 'accept' | 'dispute',
    ): void {
        if (diffItem.diffItemId == null) {
            return;
        }

        const el = this._el.nativeElement.querySelector(
            `[data-diffitemid="${diffItem.diffItemId}"]`,
        );
        if (el == null) {
            return;
        }

        el.classList.remove(`cmps-commitment-narrative--dd-note-${state}`);
    }

    // Event handlers
    /** @private */ _onToggleExpandClick(noteId: string): void {
        useDispatcher(this, toggleExpandNote({ noteId }));
        this._noteComponentDisabled = false;
    }

    /** @private */ _onAddNote(): void {
        const notes = this._getAllNotes();
        useDispatcher(this, addNote({ user: this._authService.user, notes }));
    }

    /** @private */ _onCreatedByFilterClick(user: string): void {
        let currentCreatedBy = this.state?.filterOpts?.createdBy ?? [];
        if (currentCreatedBy.includes(user)) {
            currentCreatedBy = currentCreatedBy.filter((u) => u !== user);
        } else {
            currentCreatedBy = [...currentCreatedBy, user];
        }

        useDispatcher(
            this,
            applyFilters({
                opts: { ...this.state.filterOpts, createdBy: currentCreatedBy },
            }),
        );
    }

    /** @private */ _onLastUpdatedByFilterClick(user: string): void {
        let currentLastUpdatedBy = this.state?.filterOpts?.lastUpdatedBy ?? [];
        if (currentLastUpdatedBy.includes(user)) {
            currentLastUpdatedBy = currentLastUpdatedBy.filter(
                (u) => u !== user,
            );
        } else {
            currentLastUpdatedBy = [...currentLastUpdatedBy, user];
        }

        useDispatcher(
            this,
            applyFilters({
                opts: {
                    ...this.state.filterOpts,
                    lastUpdatedBy: currentLastUpdatedBy,
                },
            }),
        );
    }

    /** @private */ _onDateRangeFilterChange(createdRange: {
        from: Date;
        to: Date;
    }): void {
        if (
            this.state?.filterOpts?.createdRange?.from === createdRange.from &&
            this.state?.filterOpts?.createdRange?.to === createdRange.to
        ) {
            createdRange = null;
        }
        useDispatcher(
            this,
            applyFilters({ opts: { ...this.state.filterOpts, createdRange } }),
        );
    }

    /** @private */ _onLastUpdatedOnFilterChange(lastUpdatedOnRange: {
        from: Date;
        to: Date;
    }): void {
        if (
            this.state?.filterOpts?.lastUpdatedOnRange?.from ===
                lastUpdatedOnRange.from &&
            this.state?.filterOpts?.lastUpdatedOnRange?.to ===
                lastUpdatedOnRange.to
        ) {
            lastUpdatedOnRange = null;
        }
        useDispatcher(
            this,
            applyFilters({
                opts: { ...this.state.filterOpts, lastUpdatedOnRange },
            }),
        );
    }

    /** @private */ _onToggleDateFilter(filterType: DateFilterType): void {
        // Ensure that enabledFilters exists and then toggle the filter type
        const isEnabled =
            this.state.filterOpts.enabledFilters?.[filterType] ?? false;
        const newEnabledFilters = {
            ...this.state.filterOpts.enabledFilters,
            [filterType]: !isEnabled,
        };

        useDispatcher(
            this,
            applyFilters({
                opts: {
                    ...this.state.filterOpts,
                    enabledFilters: newEnabledFilters,
                },
            }),
        );
    }

    // Private methods
    private _disableNoteComponentAndUpdateCD(): void {
        this._noteComponent.setDisabledState(this._disabled);
        this._noteComponentDisabled = true;
        this._cdr.detach();
        setTimeout(() => {
            this._cdr.reattach();
        });
    }

    private _focusOnNote(): void {
        if (this.state.focusNoteId != null) {
            this._noteComponent?.focusLastNote(this.state.focusNoteId);
            this._shouldFocusOnNote = false;
        }
    }

    private _initRowsSubscription(): void {
        useSelector<Array<Row<RowContext, CellContext>>>(
            'coachingDetail.commitmentNarrative.rows',
            () => null,
        )
            .pipe(
                pairwise(),
                tap(([prevRows, currRows]) => {
                    // Check if there's a change in the rows content
                    const rowsChanged = prevRows.some((row, index) => {
                        const prevNotes = row.ctx.notes;
                        const currNotes = currRows[index]?.ctx.notes;

                        if (currNotes === undefined) {
                            return true;
                        }

                        return (
                            JSON.stringify(prevNotes) !==
                            JSON.stringify(currNotes)
                        );
                    });

                    if (rowsChanged) {
                        this._propagateChange();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _initFilterRowsSubscription(): void {
        useSelector<Array<Row<RowContext, CellContext>>>(
            'coachingDetail.commitmentNarrative.filteredRow',
            () => null,
        )
            .pipe(
                pairwise(),
                filter(
                    ([prevRows, currRows]) =>
                        prevRows.length !== currRows.length,
                ),
                tap(([prevRows, currRows]) => {
                    this._populateForm(currRows);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _initFocusNoteSubscription(): void {
        useSelector<string>(
            'coachingDetail.commitmentNarrative.focusNoteId',
            () => null,
        )
            .pipe(
                filter(
                    (focusNoteId) => focusNoteId !== null && focusNoteId !== '',
                ),
                tap(() => {
                    this._shouldFocusOnNote = true;
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _populateForm(rows: Array<Row<RowContext, CellContext>>): void {
        const rowsControl = this._form.form.controls[
            this._form.fields.NOTES.name
        ] as FormArray;

        this._controlSub.forEach((sub) => sub.unsubscribe());
        this._controlSub.clear();

        rowsControl.clear();
        rows.forEach((row) => {
            const newRow = this._formBuilder.group({
                id: [row.id],
                createdBy: [row.ctx.createdBy],
                date: [row.ctx.date],
                context: [row.ctx.context],
                notes: this._formBuilder.group({
                    value: [row.ctx.notes],
                }),
            });
            rowsControl.push(newRow);
            this._subscribeToValueChanges(newRow);
        });
    }

    private _subscribeToValueChanges(group: FormGroup): void {
        this._controlSub.get(group)?.unsubscribe();

        const notesControl = group.get('notes')?.get('value');
        const id = group.get('id')?.value;

        if (notesControl != null) {
            const subscription = notesControl.valueChanges
                .pipe(
                    tap((value) => {
                        if (Array.isArray(value) && value.length > 0) {
                            useDispatcher(
                                this,
                                updateOrRemoveNote({
                                    id,
                                    content: value[0].contents,
                                    actionType: RowActionType.UpdateContent,
                                }),
                            );
                        }
                    }),
                    filter((val) => Array.isArray(val) && val.length === 0),
                    delay(0),
                )
                .subscribe(() => {
                    useDispatcher(
                        this,
                        updateOrRemoveNote({
                            id,
                            actionType: RowActionType.Remove,
                        }),
                    );

                    const rowsControl = this._form.form.controls[
                        this._form.fields.NOTES.name
                    ] as FormArray;
                    const rowIndex = rowsControl.controls.findIndex(
                        (ctrl) => ctrl === group,
                    );
                    if (rowIndex > -1) {
                        rowsControl.removeAt(rowIndex);
                    }

                    this._redecorate();
                });

            this._controlSub.set(group, subscription);
        }
    }

    private _propagateChange(): void {
        if (this._onChange == null) return;
        const allNotes = this._mergeFormChangesIntoRows();
        this._onChange(allNotes);
    }

    private _getAllNotes(): NoteDetailFormValue[] {
        const rowsControl = this._form.form.controls[
            this._form.fields.NOTES.name
        ] as FormArray;

        return rowsControl.controls
            .filter((group) => group instanceof FormGroup)
            .map((group) => this._getNoteValuesFromGroup(group as FormGroup))
            .filter((noteValues) => noteValues != null)
            .flat();
    }

    private _getNoteValuesFromGroup(
        group: FormGroup,
    ): NoteDetailFormValue[] | null {
        const notesControl = group.get('notes');
        if (notesControl != null && notesControl instanceof FormGroup) {
            const eControl = notesControl.get('value');
            if (eControl != null) return eControl.value;
        }
        return null;
    }

    private _mergeFormChangesIntoRows(): NoteDetailFormValue[] {
        const formNotes = this._getAllNotes();
        const mergedRows = this.state.rows.map((row) => {
            const formNote = formNotes.find(
                (note) => note.trackingId.toString() === row.id,
            );
            if (formNote != null) {
                return {
                    ...row,
                    ctx: {
                        ...row.ctx,
                        notes: row.ctx.notes.map((n) =>
                            n.noteId === formNote.noteId ? formNote : n,
                        ),
                    },
                };
            }
            return row;
        });

        return mergedRows.flatMap((row) => row.ctx.notes);
    }

    private _redecorate(): void {
        this._sub?.unsubscribe();
        if (this._form == null) {
            return;
        }
        this._sub = this._form.form.controls[
            this._form.fields.NOTES.name
        ].valueChanges
            .pipe(
                tap(() => {
                    this._ngZone.onStable.pipe(take(1)).subscribe({
                        complete: () =>
                            this._diffDecoratorService.redecorate(this),
                    });
                }),
            )
            .subscribe();
    }

    trackByFn(index, item): void {
        return item.id;
    }
}
